// TabBar.tsx
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Select, Badge, Button, Dropdown, Menu, Modal, Input } from "antd";
import { useProjectContext } from "../../context/ProjectContext";
import { useDirtyContext } from "../../context/DirtyContext";
import { useBadgeContext } from "../../context/BadgeContext";
import { HomeOutlined, MenuOutlined, PlusCircleOutlined } from "@ant-design/icons";
import "./TabBar.css";
import icon from "../../images/img.png";
import TransferProjectView from "../../pages/configuration/abacusToNetworkTransfer/transferProjectView/mainView/TransferProjectView";
import CreateProjectWizard from "../../pages/configuration/constructionSectionParser /wizard/CreateProjectWizard";
import { showUnsavedChangesConfirm } from "../../utils/UI";
import axios from "axios";
import { getConfig } from "../../config";

const { Option } = Select;

const SECRET = "MAG"; // Passwort für den Geheimzugang

const { API_BASE_URL } = getConfig();

const TabBar: React.FC = () => {
    const { badgeCounts } = useBadgeContext();
    const { confirmNavigation } = useDirtyContext();
    const navigate = useNavigate();

    const {
        selectedProject,
        setSelectedProject,
        managers,
        selectedManager,
        setSelectedManager,
        filteredProjects,
        // wichtig: hier holen wir uns die Nummer des aktuellen Users / PL
        selectedManagerNo,
        setSelectedManagerNo,
    } = useProjectContext();

    const [selectedMenuItem, setSelectedMenuItem] = useState<"wizard" | "transfer" | null>(null);

    // --- Passwort-Modal State ---
    const [pwOpen, setPwOpen] = useState(false);
    const [pw, setPw] = useState("");
    const [pwErr, setPwErr] = useState<string | null>(null);

    // --- Avatar-URL des angemeldeten Users ---
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

    // effektive User-ID: erst aus Context, sonst aus localStorage
    const [effectiveUserId, setEffectiveUserId] = useState<string | number | null>(null);

    // 1. Schritt: ermitteln, wer "der angemeldete Benutzer" ist
    useEffect(() => {
        let id: string | number | null = null;
        const fromStorage =
            localStorage.getItem("selectedProjectManagerNo")
        if (fromStorage) {
            id = fromStorage;
        }

        setEffectiveUserId(id);
    }, []);

    // 2. Schritt: Avatar für diese effektive User-ID laden
    useEffect(() => {
        if (!effectiveUserId) {
            return;
        }

        console.log("Lade Avatar für Benutzer:", effectiveUserId);

        const controller = new AbortController();

        axios
            .get<string>(`${API_BASE_URL}/getSlackProfilePictureByNumber`, {
                params: {
                    auth: "VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU",
                    userId: effectiveUserId,
                },
                signal: controller.signal as any,
            })
            .then((res) => {
                if (res.data) {
                    const cleaned = String(res.data).replace(/^"|"$/g, "").trim();
                    setAvatarUrl(cleaned || null);
                } else {
                    setAvatarUrl(null);
                }
            })
            .catch((err: any) => {
                if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
                    console.error("Fehler beim Laden des Slack-Profilbilds:", err);
                }
                setAvatarUrl(null);
            });

        return () => controller.abort();
    }, [API_BASE_URL, effectiveUserId]);

    const handleManagerChange = (manager: string | null) => {
        setSelectedManager(manager);
        setSelectedProject("");

        // wenn du willst, kannst du hier auch selectedManagerNo setzen,
        // falls die Manager-No == User-No ist
        // setSelectedManagerNo( ... );
    };

    const handleProjectChange = (value: string) => {
        confirmNavigation(() => setSelectedProject(value));
    };

    const handleNavigation = (path: string) => {
        confirmNavigation(() => {
            setTimeout(() => navigate(path), 0);
        });
    };

    // Rechtsklick-Handler für den Geheimzugang
    const openPwModal: React.MouseEventHandler = (e) => {
        e.preventDefault(); // verhindert normale Navigation
        e.stopPropagation(); // verhindert Browser-Kontextmenü
        setPw("");
        setPwErr(null);
        setPwOpen(true);
    };

    const confirmPw = () => {
        if (pw === SECRET) {
            setPwOpen(false);
            // nach kurzer Verzögerung navigieren (Modal schließen lassen)
            setTimeout(() => navigate("/ai-chatbot"), 0);
        } else {
            setPwErr("Falsches Passwort.");
        }
    };

    const sortedProjects = React.useMemo(() => {
        return [...filteredProjects].sort((a, b) => {
            const ai = parseInt(String(a.number), 10);
            const bi = parseInt(String(b.number), 10);
            if (!isNaN(ai) && !isNaN(bi)) return bi - ai; // numerisch absteigend
            return String(b.number).localeCompare(String(a.number), "de", {
                numeric: true,
                sensitivity: "base",
            });
        });
    }, [filteredProjects]);

    // Erweiterte Version: optional zusätzliche Anchor-Props (z. B. onContextMenu)
    const renderNavLink = (
        to: string,
        label: string,
        extra?: React.HTMLAttributes<HTMLAnchorElement>
    ) => (
        <NavLink
            to={to}
            className={({ isActive }) => (isActive ? "active" : "")}
            onClick={(e) => {
                e.preventDefault();
                handleNavigation(to);
            }}
            {...extra}
        >
            <span style={{ position: "relative", display: "inline-block" }}>
                {label}
                {badgeCounts[to] ? (
                    <Badge
                        count={badgeCounts[to]}
                        size="small"
                        style={{
                            position: "absolute",
                            top: "-18px",
                            right: "-18px",
                            fontSize: "9px",
                            height: "14px",
                            minWidth: "14px",
                            lineHeight: "14px",
                            padding: "0 4px",
                        }}
                    />
                ) : null}
            </span>
        </NavLink>
    );

    const menu = (
        <Menu
            onClick={({ key }) => {
                if (key === "home") {
                    handleNavigation("/"); // führt zur MainPage
                } else {
                    setSelectedMenuItem(key as "wizard" | "transfer");
                }
            }}
            style={{ minWidth: 200 }}
        >
            <Menu.Item
                key="wizard"
                icon={<PlusCircleOutlined style={{ color: "#153e8c" }} />}
                style={{ textAlign: "left", paddingLeft: "10px" }}
            >
                Neuerstellung
            </Menu.Item>
            <Menu.Item
                key="home"
                icon={<HomeOutlined style={{ color: "#153e8c" }} />}
                style={{ textAlign: "left", paddingLeft: "10px" }}
            >
                Startseite
            </Menu.Item>
        </Menu>
    );

    return (
        <>
            {/* Top Navigation Bar */}
            <div className="tab-bar-container" style={{ zoom: 0.9 }}>
                <div className="tab-bar-content">
                    {/* Linker Block: Menu + Selects */}
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <Dropdown overlay={menu} trigger={["click"]} placement="bottomLeft">
                            <Button icon={<MenuOutlined />} />
                        </Dropdown>

                        <Select
                            showSearch
                            allowClear
                            filterOption={(input, option) =>
                                (option?.children?.toString().toLowerCase() ?? "").includes(
                                    input.toLowerCase()
                                )
                            }
                            style={{ width: 180 }}
                            value={selectedManager || "Alle"}
                            onChange={handleManagerChange}
                            placeholder="Projektleiter auswählen"
                        >
                            {managers.map((manager) => (
                                <Option key={manager} value={manager}>
                                    {manager}
                                </Option>
                            ))}
                        </Select>

                        <Select
                            showSearch
                            allowClear
                            filterOption={(input, option) =>
                                (option?.children?.toString().toLowerCase() ?? "").includes(
                                    input.toLowerCase()
                                )
                            }
                            style={{ width: 250 }}
                            value={selectedProject}
                            onChange={handleProjectChange}
                            placeholder="Projekt auswählen"
                        >
                            <Option key="Alle" value="Alle">
                                Alle
                            </Option>
                            {sortedProjects.map((option) => (
                                <Option key={option.number} value={option.number}>
                                    {option.numberShortInfo}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    {/* Navigation Links */}
                    <nav>
                        <ul className="tab-bar">
                            <li>{renderNavLink("/configuration", "Konfiguration")}</li>
                            <li>{renderNavLink("/task-management", "Task-Management")}</li>
                            <li>{renderNavLink("/internal-orders", "Interne Beauftragungen")}</li>
                            <li>{renderNavLink("/open-internal-orders", "Offene Beauftragungen")}</li>
                            <li>{renderNavLink("/system-release", "Systemfreigabe")}</li>

                            {/* Normales Link-Verhalten bei Linksklick, Geheimzugang bei Rechtsklick */}
                            <li>
                                {renderNavLink("/tfHour-release", "24-Stunden Regel", {
                                    onContextMenu: openPwModal,
                                    title: "24h Regel",
                                })}
                            </li>

                            {/* Versteckter AI-Tab: NICHT anzeigen */}
                            {/* <li>{renderNavLink("/ai-chatbot", "AI Chatbot (Beta)")}</li> */}
                        </ul>
                    </nav>

                    {/* Rechter Block: Avatar + Logo */}
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        {/* Fancy User Avatar (rechts, links vom Logo) */}
                        <div className="user-avatar-wrapper" title="Angemeldeter Benutzer">
                            {avatarUrl ? (
                                <img
                                    src={avatarUrl}
                                    alt="User Avatar"
                                    className="user-avatar-image"
                                />
                            ) : (
                                <div className="user-avatar-placeholder">
                                    {/* Fallback, z.B. Initiale(n) – hier einfach 'M' als Platzhalter */}
                                    <span>M</span>
                                </div>
                            )}
                        </div>

                        {/* Logo/Icon */}
                        <div className="tab-bar-icon">
                            <img src={icon} alt="Icon" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal for Wizard & Transfer View */}
            <Modal
                title={selectedMenuItem === "wizard" ? "Neuerstellung (Wizard)" : "Netzplan-Transfer"}
                open={!!selectedMenuItem}
                onCancel={() => setSelectedMenuItem(null)}
                footer={null}
                width="98vw"
                style={{ top: "2.5vh", height: "95vh", zIndex: 1050 }}
                styles={{ body: { height: "85vh", overflowY: "auto" } }}
            >
                {selectedMenuItem === "wizard" && (
                    <CreateProjectWizard
                        onClose={() => showUnsavedChangesConfirm(() => setSelectedMenuItem(null))}
                    />
                )}
                {selectedMenuItem === "transfer" && <TransferProjectView />}
            </Modal>

            {/* Passwort-Modal */}
            <Modal
                title="Beta-Funktionen"
                open={pwOpen}
                onOk={confirmPw}
                onCancel={() => setPwOpen(false)}
                okText="Öffnen"
                cancelText="Abbrechen"
            >
                <p>Bitte Passwort eingeben, um den AI-Chatbot zu öffnen.</p>
                <Input.Password
                    autoFocus
                    placeholder="Passwort"
                    value={pw}
                    onChange={(e) => {
                        setPw(e.target.value);
                        if (pwErr) setPwErr(null);
                    }}
                    onPressEnter={confirmPw}
                />
                {pwErr && <div style={{ color: "red", marginTop: 8 }}>{pwErr}</div>}
            </Modal>
        </>
    );
};

export default TabBar;
